import { Bookmark, ChartBar, DotsThree as Ellipsis, Heart, ChatCircle as MessageSquare, ShareNetwork as Share2 } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { Post } from "@/api/types";
import { useBookmarkPost, useLikePost } from "@/api/queries/posts";
import { Img } from "@/components/media/Img";
import { Avatar, Card, IconButton, Menu } from "@/components/ui";
import { formatPrice, formatRelative } from "@/lib/format";
import { toast } from "@/stores/toast";

function copyLink(id: string) {
  void navigator.clipboard?.writeText(`${window.location.origin}/feed#${id}`);
  toast.success("Link copied");
}

export function PostCard({ post, onHide }: { post: Post; onHide?: (id: string) => void }) {
  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();
  const toggleLike = () => {
    likeMutation.mutate({
      postId: post.id,
      liked: !post.liked,
      authorUsername: post.author.username,
      communitySlug: post.community?.slug,
    });
  };
  const toggleBookmark = () => {
    bookmarkMutation.mutate({
      postId: post.id,
      bookmarked: !post.bookmarked,
      authorUsername: post.author.username,
      communitySlug: post.community?.slug,
    });
  };
  return (
    <article>
      <Card padded={false} className="rounded-none border-x-0 border-t-0 bg-transparent px-6 py-5">
        <div className="flex items-start gap-3">
          <Link to="/@{$username}" params={{ username: post.author.username }} className="mt-0.5 shrink-0">
            <Avatar size="lg" imageKey={post.author.avatar.key} verified={post.author.verified} alt={post.author.displayName} />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <p className="min-w-0 truncate text-post-meta">
                <Link to="/@{$username}" params={{ username: post.author.username }} className="font-bold text-text-primary">
                  {post.author.displayName}
                </Link>
                <span className="ml-1.5 text-text-muted">@{post.author.username} · {formatRelative(post.createdAt)}</span>
              </p>
              <Menu
                trigger={<IconButton label="Post options" icon={<Ellipsis size={18} />} size="sm" className="rounded-full bg-transparent hover:bg-surface-2 focus-visible:bg-surface-2" />}
                items={[
                  { label: "Copy link", onSelect: () => copyLink(post.id) },
                  { label: "Not interested", onSelect: () => onHide?.(post.id) },
                ]}
              />
            </div>
            <p className="mt-0.5 whitespace-pre-line text-post-body">{post.body}</p>
            {post.kind === "media" && post.media && (
              <Img
                imageKey={post.media.key}
                alt={post.media.alt}
                aspect="16/9"
                sizes="(min-width: 1280px) 600px, 100vw"
                className="mt-3 rounded-md"
              />
            )}
            {post.kind === "product" && post.product && (
              <Link
                to="/p/$slug"
                params={{ slug: post.product.slug }}
                className="mt-3 flex items-center gap-3 rounded-sm border border-border p-2"
              >
                <Img
                  imageKey={post.product.image.key}
                  alt={post.product.image.alt}
                  aspect="4/3"
                  sizes="64px"
                  className="w-16 shrink-0 rounded-sm"
                />
                <span className="min-w-0">
                  <span className="block text-small font-medium">{post.product.title}</span>
                  <span className="text-small tnum">
                    {formatPrice(post.product.price)} · ★ {post.product.rating}
                  </span>
                </span>
              </Link>
            )}
            <div className="mt-3 flex items-center gap-3 text-text-secondary">
              <div className="grid min-w-0 flex-1 grid-cols-2 sm:grid-cols-3">
                <span aria-label={`${post.comments} comments`} className="flex items-center gap-1.5">
                  <MessageSquare size={19} />
                  <span className="tnum">{post.comments}</span>
                </span>
                <button
                  type="button"
                  aria-label="Like post"
                  aria-pressed={post.liked}
                  onClick={toggleLike}
                  className={`flex items-center gap-1.5 ${post.liked ? "text-danger" : ""}`}
                >
                  <Heart size={19} weight={post.liked ? "fill" : "regular"} />
                  <span className="tnum">{post.likes}</span>
                </button>
                <span aria-label={`${post.likes * 24} views`} className="hidden items-center gap-1.5 sm:flex"><ChartBar size={19} /><span className="tnum">{post.likes * 24}</span></span>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                <button
                  type="button"
                  aria-label="Bookmark post"
                  aria-pressed={post.bookmarked}
                  onClick={toggleBookmark}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Bookmark size={19} weight={post.bookmarked ? "fill" : "regular"} />
                </button>
                <button
                  type="button"
                  aria-label="Copy link"
                  onClick={() => copyLink(post.id)}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Share2 size={19} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </article>
  );
}
